import React from 'react';
import { ArrowRight, ChevronDown, Layers, TrendingUp, MessageSquare, Mic, Flag } from 'lucide-react';
import BackgroundStack from './background/BackgroundStack';
import { useI18n } from '../i18n';
import LanguageSelector from './LanguageSelector';

const IntroductionScreen = ({ onContinue, selectedLanguage, onLanguageChange }) => {
    const { t } = useI18n();

    const steps = [
        { icon: Layers, title: t('introStep1Title'), desc: t('introStep1Desc'), num: '01' },
        { icon: TrendingUp, title: t('introStep2Title'), desc: t('introStep2Desc'), num: '02' },
        { icon: MessageSquare, title: t('introStep3Title'), desc: t('introStep3Desc'), num: '03' },
        { icon: Mic, title: t('introStep4Title'), desc: t('introStep4Desc'), num: '04', highlight: true },
    ];

    return (
        <BackgroundStack
            className="flex h-[100dvh] min-h-0 w-full max-h-[100dvh] flex-col"
            sidebarExpanded={false}
            scrollable={false}
        >

            <div className="shrink-0 bg-white/80 backdrop-blur-sm border-b border-stone-200 z-20">
                <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: '#1A4731' }}>
                            <span className="text-white font-bold text-sm">⚡</span>
                        </div>
                        <div>
                            <span className="font-semibold text-stone-800 text-sm">SimAI</span>
                            <span className="text-stone-300 text-sm mx-2">|</span>
                            <span className="text-stone-500 text-sm">{t('idbAse')}</span>
                        </div>
                    </div>
                    {selectedLanguage && onLanguageChange && (
                        <LanguageSelector
                            selectedLanguage={selectedLanguage}
                            onLanguageChange={onLanguageChange}
                        />
                    )}
                </div>
            </div>

            {/* Scroll interno: el CTA siempre es alcanzable en cualquier altura */}
            <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden overscroll-y-contain">
                <div className="flex flex-col items-center text-center px-6 py-8 sm:py-12 pb-4 max-w-5xl mx-auto w-full">
                <div className="w-full flex flex-col items-center">

                <div className="inline-flex items-center gap-2 px-4 py-2 bg-white rounded-full border border-stone-200 shadow-sm mb-6 sm:mb-8">
                    <Layers className="w-4 h-4" style={{ color: '#1A4731' }} />
                    <span className="text-xs font-semibold text-stone-600 uppercase tracking-widest">{t('safeguards')}</span>
                </div>

                <h1 className="text-3xl sm:text-5xl lg:text-[3.5rem] font-bold text-stone-900 mb-2 tracking-tight leading-tight max-w-2xl">
                    {t('introTitle1')}
                </h1>
                <h1 className="text-3xl sm:text-5xl lg:text-[3.5rem] font-bold mb-4 sm:mb-6 tracking-tight leading-tight max-w-2xl" style={{ color: '#1A4731' }}>
                    {t('introTitle2')}
                </h1>
                <p className="text-stone-500 text-sm sm:text-base max-w-xl mb-8 sm:mb-14 leading-relaxed">
                    {t('introSubtitle')}
                </p>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 max-w-3xl w-full mb-8 sm:mb-12 items-stretch">
                    {steps.map(({ icon: Icon, title, desc, num, highlight }) => (
                        <div
                            key={num}
                            aria-hidden={highlight ? 'true' : undefined}
                            className={`relative rounded-2xl p-4 sm:p-5 text-left select-none cursor-default overflow-hidden shadow-sm ${
                                highlight
                                    ? 'text-white'
                                    : 'bg-white border border-stone-200'
                            }`}
                            style={highlight ? {
                                background: '#1A4731',
                                backgroundImage: 'radial-gradient(rgba(255,255,255,0.08) 1px, transparent 1px)',
                                backgroundSize: '10px 10px',
                            } : undefined}
                        >
                            <div className="flex items-start justify-between mb-4 relative">
                                <div
                                    className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                                        highlight ? 'bg-white/15' : ''
                                    }`}
                                    style={!highlight ? { background: '#E8F5EE' } : undefined}
                                >
                                    <Icon className="w-5 h-5" style={{ color: highlight ? '#ffffff' : '#1A4731' }} />
                                </div>
                                {highlight ? (
                                    <Flag className="w-4 h-4 text-white/70" strokeWidth={2.25} />
                                ) : (
                                    <span className="text-[11px] font-semibold text-stone-300">{num}</span>
                                )}
                            </div>
                            <h3 className={`text-sm font-bold mb-1 relative ${highlight ? 'text-white' : 'text-stone-800'}`}>{title}</h3>
                            <p className={`text-xs leading-relaxed relative ${highlight ? 'text-white/75' : 'text-stone-400'}`}>{desc}</p>
                        </div>
                    ))}
                </div>
                </div>
                </div>
            </div>

            {/* CTA fijo: siempre visible; no depende de hacer scroll */}
            <div
                className="shrink-0 z-30 border-t border-stone-200/80 bg-white/95 shadow-[0_-8px_30px_rgba(0,0,0,0.06)] backdrop-blur-md"
                style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom, 0px))' }}
            >
                <div className="max-w-lg mx-auto px-4 pt-3 pb-3 flex flex-col items-center gap-2">
                    <ChevronDown className="w-5 h-5 text-stone-400 animate-bounce motion-reduce:animate-none shrink-0" aria-hidden />
                    <p className="text-[11px] sm:text-xs text-center text-stone-600 leading-snug px-1">
                        {t('introCtaBarHint')}
                    </p>
                    <button
                        type="button"
                        onClick={onContinue}
                        className="group flex w-full max-w-md items-center justify-center gap-3 px-6 py-3.5 rounded-full font-semibold text-white text-sm sm:text-base transition-all hover:opacity-90 hover:scale-[1.01] shadow-lg"
                        style={{ background: '#1A4731' }}
                    >
                        {t('startPractice')}
                        <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-0.5 shrink-0" />
                    </button>
                    <p className="text-[10px] sm:text-xs text-center text-stone-400 leading-snug">{t('sessionTime')}</p>
                </div>
            </div>
        </BackgroundStack>
    );
};

export default IntroductionScreen;
